import { useState, useEffect, useCallback } from "react";
import { useApi } from "../../hooks/useApi";
import { GoogleSheetMid } from "../../types/googleSheetMid";
import CreatableSelect from "../common/CreatableSelect";

interface SheetsMidSelectProps {
  companyId: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export default function SheetsMidSelect({
  companyId,
  value,
  onChange,
  disabled = false,
}: SheetsMidSelectProps) {
  const { get, post } = useApi();
  const [mids, setMids] = useState<string[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchMids = useCallback(async () => {
    try {
      const records = await get<GoogleSheetMid[]>(`/api/companies/${companyId}/google-sheet-mids`);
      setMids(records.map((record) => record.mid));
      setFetchError(null);
    } catch (err: any) {
      setFetchError(err.message || "Failed to load MIDs");
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchMids();
  }, [fetchMids]);

  const handleCreate = async (mid: string) => {
    const created = await post<GoogleSheetMid>("/api/alerts/google-sheet-mids", { companyId, mid });
    setMids((previous) => [...previous, created.mid]);
    return created.mid;
  };

  const options = [...new Set(value ? [...mids, value] : mids)].sort();

  return (
    <CreatableSelect
      label="Sheets MID"
      value={value}
      options={options.map((mid) => ({ value: mid, label: mid }))}
      onChange={onChange}
      onCreate={handleCreate}
      addLabel="Add New MID"
      createPlaceholder="MID"
      createFailedMessage="Failed to create MID"
      disabled={disabled}
      helperText={fetchError ?? undefined}
      error={!!fetchError}
    />
  );
}
